import { useEffect, useMemo, useState } from "react";
import { api, eur, frDate, openPdf, downloadPdf } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, ArrowDownLeft, ArrowUpRight, Filter, Download, FileDown, FileSpreadsheet, FileText, Upload, Loader2,
} from "lucide-react";

const empty = {
  date: new Date().toISOString().slice(0, 10),
  type: "entree", category: "", description: "",
  amount: 0, payment_method: "Virement", reference: "", attachment_note: "",
};

const CATS_IN = ["Cotisations", "Dons", "Subventions", "Prestations", "Ventes", "Autres recettes"];
const CATS_OUT = ["Salaires", "Charges sociales", "Loyer", "Fournitures", "Événements", "Transport", "Communication", "Autres dépenses"];

export default function Comptabilite() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);
  const [typeFilter, setTypeFilter] = useState("all");

  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = () => api.get("/transactions").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const runPreview = async (file) => {
    if (!file) return;
    setImporting(true); setImportPreview(null); setImportFile(file);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/transactions/import-preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportPreview(data);
      if (!data.count) toast.error("Aucune opération détectée dans ce fichier.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fichier CSV invalide");
    } finally { setImporting(false); }
  };

  const commitImport = async () => {
    if (!importPreview?.rows?.length) return;
    setImporting(true);
    try {
      const { data } = await api.post("/transactions/import-commit", { rows: importPreview.rows });
      toast.success(`${data.inserted} opération(s) importée(s).`);
      setImportOpen(false); setImportFile(null); setImportPreview(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'import");
    } finally { setImporting(false); }
  };

  const save = async () => {
    if (!form.description || !form.category) { toast.error("Catégorie et description requises"); return; }
    try {
      const payload = { ...form, amount: Number(form.amount) || 0 };
      if (edit) await api.put(`/transactions/${edit.id}`, payload);
      else await api.post("/transactions", payload);
      toast.success(edit ? "Opération modifiée." : "Opération enregistrée.");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async (t) => {
    if (!window.confirm("Supprimer cette opération ?")) return;
    await api.delete(`/transactions/${t.id}`); load();
  };

  const filtered = useMemo(
    () => rows.filter((t) => typeFilter === "all" || t.type === typeFilter),
    [rows, typeFilter]
  );

  const totals = useMemo(() => {
    const e = rows.filter(t => t.type === "entree").reduce((a, b) => a + Number(b.amount || 0), 0);
    const s = rows.filter(t => t.type === "sortie").reduce((a, b) => a + Number(b.amount || 0), 0);
    return { e, s, solde: e - s };
  }, [rows]);

  return (
    <div data-testid="comptabilite-page">
      <PageHeader
        eyebrow="Finances"
        title="Comptabilité — Entrées & sorties"
        description="Saisissez toutes les opérations financières de l'association pour suivre votre trésorerie."
        actions={
          <div className="flex gap-2">
            <Button data-testid="import-csv-btn" variant="outline" onClick={() => { setImportOpen(true); setImportFile(null); setImportPreview(null); }}>
              <Upload size={16} className="mr-1" /> Importer CSV
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="export-compta-btn" variant="outline">
                  <Download size={16} className="mr-1" /> Exporter
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem data-testid="export-pdf" onClick={() => openPdf(`/transactions/export-pdf?type_filter=${typeFilter}`)}>
                  <FileText size={14} className="mr-2" /> PDF (livre comptable)
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="export-excel" onClick={() => downloadPdf(`/transactions/export?format=xlsx&type_filter=${typeFilter}`, "comptabilite.xlsx")}>
                  <FileSpreadsheet size={14} className="mr-2" /> Excel (.xlsx)
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="export-csv" onClick={() => downloadPdf(`/transactions/export?format=csv&type_filter=${typeFilter}`, "comptabilite.csv")}>
                  <FileDown size={14} className="mr-2" /> CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button data-testid="new-transaction-btn" onClick={() => { setEdit(null); setForm(empty); setOpen(true); }} className="bg-brand-orange hover:bg-[#D04A06] text-white">
              <Plus size={16} className="mr-1" /> Nouvelle opération
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-brand-border rounded-xl p-5">
          <div className="small-caps">Total entrées</div>
          <div className="font-serif text-3xl text-brand-green mt-1">{eur(totals.e)}</div>
        </div>
        <div className="bg-white border border-brand-border rounded-xl p-5">
          <div className="small-caps">Total sorties</div>
          <div className="font-serif text-3xl text-brand-orange mt-1">{eur(totals.s)}</div>
        </div>
        <div className="bg-white border border-brand-border rounded-xl p-5">
          <div className="small-caps">Solde</div>
          <div className="font-serif text-3xl text-brand-navy mt-1">{eur(totals.solde)}</div>
        </div>
      </div>

      <div className="bg-white border border-brand-border rounded-xl">
        <div className="flex items-center gap-2 p-3 border-b border-brand-border">
          <Filter size={16} className="text-gray-400 ml-2" />
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              <SelectItem value="entree">Entrées uniquement</SelectItem>
              <SelectItem value="sortie">Sorties uniquement</SelectItem>
            </SelectContent>
          </Select>
          <span className="ml-auto text-xs text-gray-400">{filtered.length} opération(s)</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 small-caps">
              <tr>
                <th className="text-left py-3 px-4">Date</th>
                <th className="text-left py-3 px-4">Type</th>
                <th className="text-left py-3 px-4">Catégorie</th>
                <th className="text-left py-3 px-4">Description</th>
                <th className="text-right py-3 px-4">Montant</th>
                <th className="text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-gray-400">Aucune opération.</td></tr>}
              {filtered.map((t) => (
                <tr key={t.id} className="border-t border-brand-border hover:bg-gray-50">
                  <td className="py-3 px-4 text-gray-700">{frDate(t.date)}</td>
                  <td className="py-3 px-4">
                    {t.type === "entree"
                      ? <Badge className="bg-brand-green text-white"><ArrowDownLeft size={12} className="mr-1" />Entrée</Badge>
                      : <Badge className="bg-brand-orange text-white"><ArrowUpRight size={12} className="mr-1" />Sortie</Badge>}
                  </td>
                  <td className="py-3 px-4 text-gray-700">{t.category}</td>
                  <td className="py-3 px-4 text-gray-700">
                    <div>{t.description}</div>
                    <div className="text-xs text-gray-400">{t.payment_method} · {t.reference || "—"}</div>
                  </td>
                  <td className={`py-3 px-4 text-right font-medium ${t.type === "entree" ? "text-brand-green" : "text-brand-orange"}`}>
                    {t.type === "entree" ? "+" : "−"} {eur(t.amount)}
                  </td>
                  <td className="py-3 px-4 text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => { setEdit(t); setForm({ ...empty, ...t }); setOpen(true); }}><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(t)}><Trash2 size={14} className="text-red-500" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-brand-navy">{edit ? "Modifier l'opération" : "Nouvelle opération"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div><Label>Date *</Label><Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} /></div>
            <div><Label>Type *</Label>
              <Select value={form.type} onValueChange={(v) => setForm({...form, type: v, category: ""})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="entree">Entrée</SelectItem>
                  <SelectItem value="sortie">Sortie</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Catégorie *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>
                  {(form.type === "entree" ? CATS_IN : CATS_OUT).map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Montant (€) *</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Description *</Label><Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} /></div>
            <div><Label>Mode de paiement</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({...form, payment_method: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Virement">Virement</SelectItem>
                  <SelectItem value="Chèque">Chèque</SelectItem>
                  <SelectItem value="Espèces">Espèces</SelectItem>
                  <SelectItem value="Carte bancaire">Carte bancaire</SelectItem>
                  <SelectItem value="Prélèvement">Prélèvement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Référence / facture</Label><Input value={form.reference} onChange={(e) => setForm({...form, reference: e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Pièce jointe (note)</Label><Textarea rows={2} value={form.attachment_note} onChange={(e) => setForm({...form, attachment_note: e.target.value})} placeholder="Référence de la pièce justificative..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="save-transaction-btn" onClick={save} className="bg-brand-orange hover:bg-[#D04A06] text-white">{edit ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-3xl" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-brand-navy">Importer un relevé bancaire</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-brand-navy/5 text-sm text-gray-700">
              <div className="font-medium text-brand-navy mb-1">Formats acceptés</div>
              Fichier <b>.csv</b> (séparateur automatique <code>;</code> ou <code>,</code>). Colonnes reconnues : <b>Date</b>, <b>Libellé</b> (ou Description), <b>Débit</b>/<b>Crédit</b> ou <b>Montant</b> (+ <b>Type</b> entree/sortie), <b>Catégorie</b>, <b>Mode</b>, <b>Référence</b>.
            </div>

            <div className="flex items-center gap-3">
              <Input
                data-testid="import-file-input"
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => runPreview(e.target.files?.[0])}
                className="flex-1"
              />
              {importing && <Loader2 className="animate-spin text-brand-navy" size={18} />}
            </div>

            {importPreview && (
              <div className="border border-brand-border rounded-lg overflow-hidden">
                <div className="flex flex-wrap items-center gap-4 p-3 border-b border-brand-border bg-gray-50 text-sm">
                  <span><b>{importPreview.count}</b> opération(s) détectée(s)</span>
                  <span className="text-brand-green">Recettes : <b>{eur(importPreview.total_entrees)}</b></span>
                  <span className="text-brand-orange">Dépenses : <b>{eur(importPreview.total_sorties)}</b></span>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-gray-500 small-caps sticky top-0">
                      <tr>
                        <th className="text-left py-2 px-3">Date</th>
                        <th className="text-left py-2 px-3">Type</th>
                        <th className="text-left py-2 px-3">Catégorie</th>
                        <th className="text-left py-2 px-3">Description</th>
                        <th className="text-right py-2 px-3">Montant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.rows.map((r, i) => (
                        <tr key={i} className="border-t border-brand-border">
                          <td className="py-1.5 px-3 text-gray-700">{frDate(r.date)}</td>
                          <td className="py-1.5 px-3">
                            {r.type === "entree"
                              ? <Badge className="bg-brand-green text-white">Recette</Badge>
                              : <Badge className="bg-brand-orange text-white">Dépense</Badge>}
                          </td>
                          <td className="py-1.5 px-3 text-gray-700">{r.category}</td>
                          <td className="py-1.5 px-3 text-gray-700 max-w-xs truncate">{r.description}</td>
                          <td className={`py-1.5 px-3 text-right font-medium ${r.type === "entree" ? "text-brand-green" : "text-brand-orange"}`}>
                            {eur(r.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Annuler</Button>
            <Button
              data-testid="confirm-import-btn"
              onClick={commitImport}
              disabled={!importPreview?.count || importing}
              className="bg-brand-orange hover:bg-[#D04A06] text-white"
            >
              {importing ? <Loader2 className="animate-spin mr-1" size={14} /> : <Upload size={14} className="mr-1" />}
              Importer {importPreview?.count ? `(${importPreview.count})` : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
